import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { ExerciseReviewData } from "./types";
import ExerciseReviewContent from "./ExerciseReviewContent";
import "./SolutionAnalysisModal.scss";

export interface SolutionAnalysisModalProps {
  open: boolean;
  onClose: () => void;
  data: ExerciseReviewData | null;
  onOpenExercise?: (exerciseId: number) => void;
}

export const SolutionAnalysisModal: React.FC<SolutionAnalysisModalProps> = ({
  open,
  onClose,
  data,
  onOpenExercise,
}) => {
  const { t } = useTranslation();

  // Keyboard escape listener
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open || !data) return null;

  return (
    <div
      className="solution-analysis-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="solution-analysis-modal-title"
      data-testid="student-solution-analysis-modal"
    >
      {/* Dimmed Backdrop */}
      <div
        className="solution-analysis-modal__backdrop"
        onClick={onClose}
        onTouchMove={(e) => e.preventDefault()}
        aria-hidden="true"
        data-testid="solution-analysis-modal-backdrop"
      />

      {/* Slide-Up Bottom Sheet Paper */}
      <div className="solution-analysis-modal__paper">
        {/* Visual Top Bar Indicator */}
        <div className="solution-analysis-modal__handle-bar" />

        {/* Top Header Row (RTL layout) */}
        <div className="solution-analysis-modal__header">
          {/* RTL Start (Right): Teacher Avatar & Identity */}
          <div className="solution-analysis-modal__teacher-info">
            <div className="solution-analysis-modal__avatar">
              <span>{t("solutionAnalysisShunitInitial", "ש")}</span>
            </div>

            <div className="solution-analysis-modal__titles">
              <span
                id="solution-analysis-modal-title"
                className="solution-analysis-modal__teacher-name"
              >
                {t("solutionAnalysisShunitTeacher", "שונית")}
              </span>
              <span className="solution-analysis-modal__subtitle">
                {t("solutionAnalysisMathTeacher", "המורה למתמטיקה")}
              </span>
            </div>
          </div>

          {/* RTL End (Left): Action buttons */}
          <div className="solution-analysis-modal__actions">
            <button
              type="button"
              className="solution-analysis-modal__icon-btn solution-analysis-modal__icon-btn--close"
              onClick={onClose}
              title={t("close")}
              aria-label={t("close")}
              data-testid="close-analysis-modal"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="solution-analysis-modal__body">
          <ExerciseReviewContent
            data={data}
            showTeacherComment={false}
            onOpenExercise={onOpenExercise}
          />
        </div>
      </div>
    </div>
  );
};

export default SolutionAnalysisModal;
